import type { FormSection, WorkflowField, WorkflowStep, WorkflowTemplate } from '@/api/workflows';

import type { FieldValue } from './FieldRenderer';
import { forEachSectionField, valueKeyFor } from './sectionInstances';

/**
 * A uniqueness collision, with the STEP the offending peer lives on.
 *
 * The step matters because peer labels repeat: RDSP has six fields called
 * "Mobile Phone" in one group, so naming the label alone produces
 * "This value is already used by 'Mobile Phone'" while the advisor is
 * standing on a field called Mobile Phone — which reads as the field
 * accusing itself and tells them nothing about where to go and fix it.
 */
export type UniquenessConflict = {
  peer: WorkflowField;
  step: WorkflowStep;
};

/**
 * Given a WorkflowField and the current advisor values map, walk every
 * peer field carrying the same non-empty `uniquenessGroup` across every
 * step of the workflow, and return the first peer that already holds
 * the current field's value. Returns null when the field isn't in a
 * group, when its value is blank, or when no peer collides.
 *
 * §1 Repeatable Sections: for a field placed inside a repeatable
 * section, the effective value lives at `inst:<sectionId>:0:<fieldId>`
 * (instance 0 — the callsite is FieldRenderer's on-blur which doesn't
 * carry an instance identity today). Peer walkers check EVERY instance
 * of every peer's section, so a duplicate in a repeated section blocks
 * on blur even when the peer is a sibling in the same section.
 *
 * Comparison is case-insensitive and trims whitespace — real-world
 * signatory email + phone inputs vary in casing / trailing spaces and
 * BMO treats those as identical. If we ever add a group whose values
 * are case-sensitive (e.g. serial numbers), we'll widen the API to
 * take a normalizer.
 *
 * Visibility-aware when `isFieldVisible` is supplied: hidden peers
 * don't count, so an inline on-blur error never accuses a field the
 * advisor can't see. FormShell passes its precomputed visibility
 * closure; direct callers may omit it (older paths).
 */
export function findUniquenessConflict(
  field: WorkflowField,
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  isFieldVisible?: (field: WorkflowField, stepId: string, sectionIndex: number) => boolean,
  /**
   * On-blur overlay — the incoming value the advisor is committing.
   * When set, this replaces the stored own-value in the comparison
   * without the caller needing to know which qualified key the field's
   * value lives at. Peers are still read from `values` as-is.
   */
  overrideOwnValue?: FieldValue,
  /**
   * The exact value-map key of the field being checked. For a field
   * inside a repeatable section this is the CURRENT instance's key
   * (`inst:<sectionId>:<idx>:<fieldId>`), so the walker skips the right
   * instance rather than always assuming instance 0. Omit for
   * non-repeatable fields — it falls back to the bare id / instance 0.
   * Critical for correctness: without it, editing instance ≥ 1 compares
   * the field against its own stored value and always self-collides.
   */
  ownValueKey?: string,
): UniquenessConflict | null {
  const group = field.uniquenessGroup?.trim();
  if (!group) return null;
  // Find the current field's section so we can pick the right value key.
  // Walk-and-find because passing section id through every caller would
  // ripple far; this is one blur at a time so cost is negligible.
  const ownSection = findOwningSection(field, template);
  const ownKey = ownValueKey ?? (ownSection ? valueKeyFor(ownSection, field.id) : field.id);
  const own =
    overrideOwnValue !== undefined ? normalize(overrideOwnValue) : normalize(values[ownKey]);
  if (own === null) return null;
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    for (let si = 0; si < step.sections.length; si++) {
      const section = step.sections[si];
      if (!section) continue;
      let peerHit: UniquenessConflict | null = null;
      forEachSectionField(section, values, (peerFieldId, valueKey) => {
        if (peerHit) return;
        const peer = findFieldById(section, peerFieldId);
        if (!peer) return;
        if (peer.id === field.id) {
          // Same authored field, different instance: still counts as a
          // peer for uniqueness (two AH instances sharing an email). Skip
          // ONLY the field's own instance — `ownKey` is the current
          // instance's key, so sibling instances still collide but the
          // field never flags against itself.
          if (section.id === ownSection?.id && valueKey === ownKey) {
            return;
          }
        }
        if (peer.uniquenessGroup?.trim() !== group) return;
        if (isFieldVisible && !isFieldVisible(peer, step.id, si)) return;
        const other = normalize(values[valueKey]);
        if (other === null) return;
        if (other === own) peerHit = { peer, step };
      });
      if (peerHit) return peerHit;
    }
  }
  return null;
}

/**
 * Return every uniqueness-group conflict on the workflow as pairs of
 * peer field ids. Used by FormShell's step-completion / missing-fields
 * logic to block Next when any two peers share the same value. The
 * output is a set of field ids that participate in at least one
 * collision — the caller shows an error against each. Section / field
 * visibility is respected: a hidden peer doesn't count.
 *
 * §1 Repeatable Sections: buckets receive one entry per (field,
 * instance) so two instances of the same repeatable section flagged
 * with the same email collide.
 */
export function findAllUniquenessConflicts(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  isFieldVisible: (field: WorkflowField, stepId: string, sectionIndex: number) => boolean,
): Set<string> {
  const byGroup = new Map<string, Array<{ fieldId: string; value: string }>>();
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    for (let si = 0; si < step.sections.length; si++) {
      const section = step.sections[si];
      if (!section) continue;
      forEachSectionField(section, values, (fieldId, valueKey) => {
        const f = findFieldById(section, fieldId);
        if (!f) return;
        const group = f.uniquenessGroup?.trim();
        if (!group) return;
        if (!isFieldVisible(f, step.id, si)) return;
        const norm = normalize(values[valueKey]);
        if (norm === null) return;
        const bucket = byGroup.get(group) ?? [];
        bucket.push({ fieldId: f.id, value: norm });
        byGroup.set(group, bucket);
      });
    }
  }
  const offenders = new Set<string>();
  for (const bucket of byGroup.values()) {
    const seen = new Map<string, string>();
    for (const { fieldId, value } of bucket) {
      const prevId = seen.get(value);
      if (prevId !== undefined) {
        offenders.add(prevId);
        offenders.add(fieldId);
      } else {
        seen.set(value, fieldId);
      }
    }
  }
  return offenders;
}

function findFieldById(section: FormSection, fieldId: string): WorkflowField | null {
  for (const row of section.rows) {
    for (const col of row.columns) {
      if (col.field?.id === fieldId) return col.field;
    }
  }
  return null;
}

function findOwningSection(field: WorkflowField, template: WorkflowTemplate): FormSection | null {
  for (const step of template.steps) {
    if (step.type !== 'DynamicForm') continue;
    for (const section of step.sections) {
      if (findFieldById(section, field.id)) return section;
    }
  }
  return null;
}

function normalize(v: FieldValue): string | null {
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  if (trimmed.length === 0) return null;
  return trimmed.toLowerCase();
}
